/**
 * TTL + in-flight de-duplicated async memo.
 *
 * Generalises the idiom `useObjectLabel` already uses (30s `entitiesCache`
 * plus an in-flight promise map) so the designer-tree fetch and the ref-field
 * lookup fetch can share ONE implementation instead of two hand-rolled copies.
 *
 * Failures are never cached — a rejected load clears the in-flight entry, so
 * the next caller retries.
 */
export interface TtlCache<T> {
  /** Cached value, the in-flight promise, or a fresh `load()` — in that order. */
  get(key: string, load: () => Promise<T>): Promise<T>
  /** Drop one key, or everything when called with no key. */
  invalidate(key?: string): void
}

export function createTtlCache<T>(ttlMs: number): TtlCache<T> {
  const values = new Map<string, { value: T; at: number }>()
  const inFlight = new Map<string, Promise<T>>()

  return {
    get(key, load) {
      const hit = values.get(key)
      if (hit && Date.now() - hit.at <= ttlMs) return Promise.resolve(hit.value)

      const pending = inFlight.get(key)
      if (pending) return pending

      const promise = load()
        .then((value) => {
          values.set(key, { value, at: Date.now() })
          return value
        })
        .finally(() => {
          inFlight.delete(key)
        })
      inFlight.set(key, promise)
      return promise
    },

    invalidate(key) {
      if (key === undefined) {
        values.clear()
        inFlight.clear()
        return
      }
      values.delete(key)
      inFlight.delete(key)
    },
  }
}
